/**
 * Zeitraeume fuer Statistiken (V-16): Woche, Monat, Jahr oder frei, und
 * ihre Einteilung in Abschnitte (Tage, Wochen, Monate) fuer die Diagramme.
 */
import { addDays, addMonths, differenceInDays, endOfMonth, formatDate, formatDe, isoWeek, startOfMonth, startOfWeek, toLocalDate } from '@/lib/datetime';
import type { LocalDate } from '@/domain/types';

export type ZeitraumArt = 'woche' | 'monat' | 'jahr' | 'frei';
export interface Zeitraum { von: LocalDate; bis: LocalDate }

export interface Abschnitt extends Zeitraum {
  key: string;
  /** Voller Name fuer Tooltip und Tabelle. */
  titel: string;
  /** Kurz fuer die Achse. */
  kurz: string;
}

/** Der Zeitraum dieser Art, in dem `tag` liegt (bei „frei“ die letzten 30 Tage bis `tag`). */
export function zeitraumUm(art: ZeitraumArt, tag: LocalDate): Zeitraum {
  if (art === 'woche') return { von: startOfWeek(tag), bis: addDays(startOfWeek(tag), 6) };
  if (art === 'monat') return { von: startOfMonth(tag), bis: endOfMonth(tag) };
  if (art === 'jahr') return { von: `${tag.slice(0, 4)}-01-01`, bis: `${tag.slice(0, 4)}-12-31` };
  return { von: addDays(tag, -29), bis: tag };
}

/** Vorheriger bzw. naechster Zeitraum gleicher Art und Laenge. */
export function verschiebe(art: ZeitraumArt, z: Zeitraum, schritt: 1 | -1): Zeitraum {
  if (art === 'woche') return { von: addDays(z.von, 7 * schritt), bis: addDays(z.bis, 7 * schritt) };
  if (art === 'monat') { const v = addMonths(z.von, schritt); return { von: v, bis: endOfMonth(v) }; }
  if (art === 'jahr') { const j = Number(z.von.slice(0, 4)) + schritt; return { von: `${j}-01-01`, bis: `${j}-12-31` }; }
  const tage = differenceInDays(z.von, z.bis) + 1;
  return { von: addDays(z.von, tage * schritt), bis: addDays(z.bis, tage * schritt) };
}

export const tageIn = (z: Zeitraum) => differenceInDays(z.von, z.bis) + 1;
export const liegtIn = (tag: LocalDate | undefined, z: Zeitraum) => !!tag && tag >= z.von && tag <= z.bis;

/** ISO-Zeitpunkt -> lokaler Kalendertag; kaputte Werte zaehlen nicht. */
export function tagVon(iso: string | undefined): LocalDate | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : toLocalDate(d);
}

/**
 * Einteilung fuer Diagramme: bis 14 Tage je Tag, bis gut vier Monate je
 * Woche (Mo-So), darueber je Monat. Randabschnitte werden auf den Zeitraum
 * gekuerzt, damit nichts ausserhalb mitzaehlt.
 */
export function abschnitte(z: Zeitraum): Abschnitt[] {
  const tage = tageIn(z);
  if (tage <= 0) return [];
  if (tage <= 14) {
    return Array.from({ length: tage }, (_, i) => {
      const d = addDays(z.von, i);
      return { key: d, von: d, bis: d, titel: formatDe(d, 'EEEEEE dd.MM.'), kurz: formatDe(d, 'EEEEEE') };
    });
  }
  const liste: Abschnitt[] = [];
  if (tage <= 125) {
    for (let s = startOfWeek(z.von); s <= z.bis; s = addDays(s, 7)) {
      const von = s < z.von ? z.von : s;
      const e = addDays(s, 6);
      const bis = e > z.bis ? z.bis : e;
      liste.push({ key: s, von, bis, titel: `KW ${isoWeek(s)} (${formatDe(von, 'dd.MM.')}–${formatDe(bis, 'dd.MM.')})`, kurz: `KW ${isoWeek(s)}` });
    }
    return liste;
  }
  for (let s = startOfMonth(z.von); s <= z.bis; s = addMonths(s, 1)) {
    const von = s < z.von ? z.von : s;
    const e = endOfMonth(s);
    liste.push({ key: s.slice(0, 7), von, bis: e > z.bis ? z.bis : e, titel: formatDe(s, 'MMMM yyyy'), kurz: formatDe(s, 'MMM') });
  }
  return liste;
}

/** Index des Abschnitts, in dem `tag` liegt, sonst -1. */
export function abschnittVon(liste: Abschnitt[], tag: LocalDate | undefined): number {
  if (!tag) return -1;
  return liste.findIndex((a) => tag >= a.von && tag <= a.bis);
}

/** Durchschnitt; `null` ohne Werte (nie 0 - ein leerer Zeitraum ist nicht schlecht). */
export function mittel(werte: number[]): number | null {
  return werte.length ? werte.reduce((s, v) => s + v, 0) / werte.length : null;
}

export const runde1 = (v: number) => Math.round(v * 10) / 10;

/** Titel eines Zeitraums, z. B. "KW 40 · 28.09.–04.10.2026" oder "Oktober 2026". */
export function zeitraumTitel(art: ZeitraumArt, z: Zeitraum): string {
  if (art === 'woche') return `KW ${isoWeek(z.von)} · ${formatDe(z.von, 'dd.MM.')}–${formatDe(z.bis, 'dd.MM.yyyy')}`;
  if (art === 'monat') return formatDe(z.von, 'MMMM yyyy');
  if (art === 'jahr') return z.von.slice(0, 4);
  return `${formatDate(z.von)} – ${formatDate(z.bis)} (${tageIn(z)} Tage)`;
}
